import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { EmbroideryLocationConfigs } from '../../../pdp/constants/embroidery';
import {
  parseEmbroideryDetails,
  mapEmbroideryDetails,
} from '../../utils/embroidery';

import {
  EmbroideryLine,
  DetailText,
  CustomizationText,
  TextCustomizations,
  LogoCustomizations,
} from './components';

const CUSTOMIZATION_LOCATION_TEMPLATE_IDS = EmbroideryLocationConfigs.reduce(
  (acc, config) => [...acc, config.customizationLocationTemplateId],
  []
);

const CUSTOMIZATION_LOCATION_LABELS = EmbroideryLocationConfigs.reduce(
  (acc, config) => [...acc, config.resourceBundle.defaultMessage],
  []
);

const EmbroideryDetailsContainer = styled.div`
  margin: 8px 0;

  ${EmbroideryLine} {
    ${({ theme, detailLineItemLabelStyles }) => css`
      color: ${theme.colors.textFadedAlternate};

      ${detailLineItemLabelStyles};
    `}

  ${DetailText} {
    ${({ detailLineItemValueStyles }) => detailLineItemValueStyles}
  }

  ${CustomizationText} {
    ${({ customizationTextStyles, detailLineItemValueStyles }) =>
      customizationTextStyles || detailLineItemValueStyles}
  }
`;

const Spacer = styled.div`
  height: 8px;
`;

const ItemEmbroideryDetails = ({
  customizationTextStyles,
  detailLineItemLabelStyles,
  detailLineItemValueStyles,
  textPersonalizations,
  iconPersonalizations,
  personalizations,
  separatorCmp,
}) => {
  const customizations = useMemo(() => {
    const parsedDetails = parseEmbroideryDetails(personalizations);
    return iconPersonalizations && textPersonalizations
      ? mapEmbroideryDetails(
          iconPersonalizations,
          textPersonalizations,
          parsedDetails
        )
      : parsedDetails;
  }, [iconPersonalizations, personalizations, textPersonalizations]);

  const hasLogo = !!customizations.logo;
  const hasText = !!(customizations.line1 || customizations.line2);

  return (
    <EmbroideryDetailsContainer
      detailLineItemLabelStyles={detailLineItemLabelStyles}
      detailLineItemValueStyles={detailLineItemValueStyles}
      customizationTextStyles={customizationTextStyles}
    >
      <TextCustomizations textCustomizations={customizations} />
      {hasLogo && hasText && (separatorCmp || <Spacer />)}
      <LogoCustomizations logoCustomizations={customizations} />
    </EmbroideryDetailsContainer>
  );
};

ItemEmbroideryDetails.propTypes = {
  customizationTextStyles: PropTypes.string,
  detailLineItemLabelStyles: PropTypes.string,
  detailLineItemValueStyles: PropTypes.string,
  iconPersonalizations: PropTypes.shape({
    productId: PropTypes.number,
    type: PropTypes.string,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        locationId: PropTypes.oneOf([1, 2]),
        label: PropTypes.oneOf(['Left Sleeve', 'Right Sleeve']),
        customizations: PropTypes.arrayOf(
          PropTypes.shape({
            customizationId: 'logo',
            label: 'logo',
            options: PropTypes.arrayOf(
              PropTypes.shape({
                optionId: PropTypes.number,
                label: PropTypes.string,
              })
            ),
          })
        ),
      })
    ),
  }),
  personalizations: PropTypes.arrayOf(
    PropTypes.shape({
      customizations: PropTypes.arrayOf(
        PropTypes.shape({
          customizationId: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      locationId: PropTypes.oneOf(CUSTOMIZATION_LOCATION_TEMPLATE_IDS),
      locationLabel: PropTypes.oneOf(CUSTOMIZATION_LOCATION_LABELS),
    })
  ),
  separatorCmp: PropTypes.node,
  textPersonalizations: PropTypes.shape({
    productId: PropTypes.number,
    type: PropTypes.string,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        locationId: PropTypes.oneOf(CUSTOMIZATION_LOCATION_TEMPLATE_IDS),
        label: PropTypes.oneOf(CUSTOMIZATION_LOCATION_LABELS),
        customizations: PropTypes.arrayOf(
          PropTypes.shape({
            customizationId: 'text',
            label: 'text',
            options: PropTypes.arrayOf(
              PropTypes.shape({
                optionId: PropTypes.number,
                label: PropTypes.string,
              })
            ),
          })
        ),
      })
    ),
  }),
};

export default ItemEmbroideryDetails;
